import * as React from "react";
import { Box, Paper, Typography, FormControl, Select, MenuItem } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

// ---- CSS Styles ------
import '../../common/styles/_utilities.scss';

// ===== JSON =====
import {getFilterConfigForProduct} from "../../common/metadata/FilterConfig";

const DISPLAY_MODE_OPTIONS = ["Visible", "Hidden", "Locked"];;

// ===== Manual grouping =====
function buildGroupedRows(json) {
    const rows = [];
    if (!json || !json.groups) return rows;

    json.groups.forEach((group) => {
        rows.push({
            id: `__group__${group.key}`,
            rowType: "GROUP",
            groupLabel: group.label,
            subgroupLabel: "",
            filterId: "",
            filterLabel: "",
            filterType: "",
        });

        group.subgroups.forEach((subgroup) => {
            const subLabel = subgroup.label || `${group.label}`;

            rows.push({
                id: `__subgroup__${group.key}__${subgroup.key}`,
                rowType: "SUBGROUP",
                groupLabel: group.label,
                subgroupLabel: subLabel,
                filterId: "",
                filterLabel: "",
                filterType: "",
            });

            if (subgroup.filters && Array.isArray(subgroup.filters)) {
                subgroup.filters.forEach((f) => {
                    const uniqueId = `${group.key}_${subgroup.key}_${f.id}`;

                    rows.push({
                        id: uniqueId,
                        rowType: "FILTER",
                        groupLabel: group.label,
                        subgroupLabel: subLabel,
                        filterId: f.id,
                        filterLabel: f.label,
                        filterType: f.filterType,
                        advanced: Boolean(f.advanced),
                        preview: Boolean(f.preview),
                        defaultDisplayMode: f.defaultDisplayMode || "Visible",
                    });
                });
            }
        });
    });

    return rows;
}

export default function OrganizationEditFilterForm(props) {
    const {
        setCustomFilters,
        customFilters,
        productKey,
    } = props;

    /**
     * filter format:
     * { FILTER_ID: "HAS_DATA", DISPLAY_MODE: "visible" }
     */
    function collectCustomFilters(filter) {
        if (!filter?.FILTER_ID) return;

        setCustomFilters((prevFilters) => {
            const currentFilters = prevFilters || [];

            const existingIndex = prevFilters?.findIndex(
                (f) => f.FILTER_ID === filter.FILTER_ID
            );

            if (existingIndex === -1) {
                return [...currentFilters, filter];
            }

            // If filter exists → update DISPLAY_MODE only
            return currentFilters.map((f, index) =>
                index === existingIndex
                    ? { ...f, DISPLAY_MODE: filter.DISPLAY_MODE }
                    : f
            );
        });
    }

    const columns = [
        {
            field: "groupLabel",
            headerName: "Group",
            flex: 1,
            renderCell: (params) => (params.row.rowType === "GROUP" ? <strong>{params.value}</strong> : ""),
        },
        {
            field: "subgroupLabel",
            headerName: "Sub Group",
            flex: 1,
            renderCell: (params) => (params.row.rowType === "SUBGROUP" ? <strong>{params.value}</strong> : ""),
        },
        {
            field: "filterId",
            headerName: "Filter ID",
            flex: 1,
            renderCell: (params) => (params.row.rowType === "FILTER" ? params.value : ""),
        },
        {
            field: "filterLabel",
            headerName: "Label",
            flex: 1.5,
            renderCell: (params) => (params.row.rowType === "FILTER" ? params.value : ""),
        },
        {
            field: "preview",
            headerName: "Preview",
            width: 100,
            // option 1: show Yes/No
            renderCell: (params) => {
                if (params?.row?.rowType !== "FILTER") return "";
                return params?.row?.preview ? "Yes" : "No";
            },
        },
        {
            field: "filterType",
            headerName: "Type",
            width: 140,
            renderCell: (params) => (params.row.rowType === "FILTER" ? params.value : ""),
        },
        {
            field: "displayMode",
            headerName: "Display Mode",
            width: 160,
            sortable: false,
            renderCell: (params) => {
                if (params.row.rowType !== "FILTER") return null;

                const currentFilter = customFilters?.find(
                    (f) => f.FILTER_ID === params.row.id
                );
                const value = currentFilter ? currentFilter.DISPLAY_MODE : params.row.defaultDisplayMode;

                return (
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <FormControl size="small" sx={{ minWidth: 110 }}>
                            <Select
                                value={value}
                                onChange={(e) => {
                                    collectCustomFilters({
                                        FILTER_ID: params.row.id,
                                        DISPLAY_MODE: e.target.value
                                    });
                                }}
                                sx={{
                                    height: 32,
                                    fontSize: 12,
                                    borderRadius: 0,
                                    "& .MuiSelect-select": {
                                        display: "flex",
                                        alignItems: "center",
                                        paddingTop: 0,
                                        paddingBottom: 0,
                                    },
                                }}
                            >
                                {DISPLAY_MODE_OPTIONS.map((opt) => (
                                    <MenuItem key={opt} value={opt} dense>
                                        {opt}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                );
            },
        },
    ];

    // Only the groups this product exposes (see GROUP_PRODUCT_TAGS in FilterConfig).
    const productGroups = React.useMemo(
        () => getFilterConfigForProduct(productKey).groups,
        [productKey]
    );
    const rowsPart1 = React.useMemo(() => buildGroupedRows({ groups: productGroups.slice(0, 5) }), [productGroups]);
    const rowsPart2 = React.useMemo(() => buildGroupedRows({ groups: productGroups.slice(5) }), [productGroups]);

    const renderTable = (rows, hideHeader = false) => (
        <Box sx={{ mb: 0}}>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 0,
                    border: "1px solid #CCC",
                    borderTop: hideHeader ? "none" : "1px solid #CCC",
                    boxShadow: "none",
                    overflow: "hidden"
                }}
            >
                <DataGrid
                    rows={rows}
                    columns={columns}
                    autoHeight
                    hideFooter
                    disableRowSelectionOnClick
                    disableColumnSorting
                    // Limit page size 100 in MIT version
                    rowHeight={45}
                    columnHeaderHeight={hideHeader ? 0 : 50}
                    initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
                    getRowClassName={(params) => {
                        if (params.row.rowType === "GROUP") return "row-group";
                        if (params.row.rowType === "SUBGROUP") return "row-subgroup";
                        return "";
                    }}
                    sx={{
                        ...gridStyles,
                        border: "none",
                        "& .MuiDataGrid-columnHeaders": {
                            display: hideHeader ? "none" : "flex",
                            backgroundColor: "#000",
                            color: '#000',
                            borderRadius: 0
                        },
                        "& .MuiDataGrid-main": {
                            borderBottom: "none",
                        },
                        "& .MuiDataGrid-columnSeparator": {
                            display: "none",
                        },
                        "& .MuiDataGrid-cell": {
                            padding: "0 8px",
                            fontSize: "13px",
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontSize: "15px",
                            fontWeight: 600,
                        }
                    }}
                />
            </Paper>
        </Box>
    );

    return (
        <Box sx={{ p: 0.5 }}>
            {renderTable(rowsPart1, false)}
            {rowsPart2.length > 0 && renderTable(rowsPart2, true)}
        </Box>
    );
}

const gridStyles = {
    "& .MuiDataGrid-cell:focus": { outline: "none" },
    "& .MuiDataGrid-columnHeaders": { backgroundColor: "#000", color: '#2d5d7e', fontWeight: 800 },
    "& .row-group": { backgroundColor: "#e3f2fd", fontWeight: 600 },
    "& .row-subgroup": { backgroundColor: "#f3f8ff", fontWeight: 400 },
    "& .MuiDataGrid-virtualScroller": { overflow: "hidden" },
};
